import React, { useEffect, useState, useCallback } from "react";
import {
  ReactFlow, Background, Controls, MiniMap, addEdge,
  useNodesState, useEdgesState, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * WorkflowBuilder — drag-and-drop status/transition editor built on @xyflow/react.
 * Nodes = task statuses. Edges = transitions with a label. Admin can add/move/delete.
 */
export default function WorkflowBuilder() {
  const [workflows, setWorkflows] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [newStatus, setNewStatus] = useState("");
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = async () => {
    const { data } = await api.get("/admin/workflows");
    setWorkflows(data);
    if (data.length && !activeId) {
      const def = data.find((w) => w.is_default) || data[0];
      pickWorkflow(def);
    }
  };

  const pickWorkflow = (wf) => {
    setActiveId(wf.workflow_id);
    setName(wf.name);
    setIsDefault(!!wf.is_default);
    setNodes(wf.nodes.map((n) => ({
      id: n.id,
      position: n.position || { x: 0, y: 0 },
      data: { label: n.label },
      type: "default",
      style: {
        borderRadius: 2,
        border: "1px solid var(--xy-node-border, #E4E4E7)",
        background: "var(--xy-node-bg, #fff)",
        color: "var(--xy-node-fg, #111)",
        fontSize: 12,
        padding: "6px 12px",
      },
    })));
    setEdges(wf.edges.map((e) => ({
      id: e.id, source: e.source, target: e.target,
      label: e.label,
      labelStyle: { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" },
      labelBgStyle: { fill: "var(--xy-edge-bg, #fff)" },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#E1252A" },
      style: { stroke: "#E1252A", strokeWidth: 1.5 },
    })));
  };

  useEffect(() => { loadList(); }, []); // eslint-disable-line

  const onConnect = useCallback((params) => {
    const label = window.prompt("Transition label (e.g. 'Approve')", "transition");
    if (label === null) return;
    setEdges((eds) => addEdge({
      ...params,
      id: `e_${Date.now()}`,
      label: label || "transition",
      labelStyle: { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#E1252A" },
      style: { stroke: "#E1252A", strokeWidth: 1.5 },
    }, eds));
  }, [setEdges]);

  const addStatusNode = () => {
    if (!newStatus.trim()) return;
    const id = newStatus.toLowerCase().replace(/\s+/g, "_");
    if (nodes.find((n) => n.id === id)) {
      toast.error("Status already exists");
      return;
    }
    setNodes((nds) => [...nds, {
      id,
      position: { x: 100 + Math.random() * 400, y: 100 + Math.random() * 300 },
      data: { label: newStatus },
      type: "default",
      style: {
        borderRadius: 2, border: "1px dashed #E1252A", background: "#fff",
        color: "#111", fontSize: 12, padding: "6px 12px",
      },
    }]);
    setNewStatus("");
  };

  const deleteSelected = () => {
    setNodes((nds) => nds.filter((n) => !n.selected));
    setEdges((eds) => eds.filter((e) => !e.selected));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        workflow_id: activeId,
        name: name || "Untitled workflow",
        is_default: isDefault,
        nodes: nodes.map((n) => ({ id: n.id, label: n.data.label, position: n.position, type: "status" })),
        edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, label: e.label })),
      };
      const { data } = await api.post("/admin/workflows", payload);
      toast.success("Workflow saved");
      setActiveId(data.workflow_id);
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const createNew = () => {
    const id = null;
    setActiveId(id);
    setName("New workflow");
    setIsDefault(false);
    setNodes([
      { id: "start", position: { x: 100, y: 100 }, data: { label: "Start" }, type: "default", style: { borderRadius: 2, border: "1px solid #E4E4E7", background: "#fff", padding: "6px 12px", fontSize: 12 } },
      { id: "end", position: { x: 400, y: 100 }, data: { label: "End" }, type: "default", style: { borderRadius: 2, border: "1px solid #E4E4E7", background: "#fff", padding: "6px 12px", fontSize: 12 } },
    ]);
    setEdges([]);
  };

  const deleteWorkflow = async () => {
    if (!activeId || activeId === "wf_default") {
      toast.error("Cannot delete the default workflow");
      return;
    }
    if (!window.confirm("Delete this workflow?")) return;
    try {
      await api.delete(`/admin/workflows/${activeId}`);
      toast.success("Workflow deleted");
      setActiveId(null);
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid="workflow-builder">
      {/* Top toolbar */}
      <div className="border border-border bg-background p-3 flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Workflow</Label>
          <Select value={activeId || ""} onValueChange={(v) => {
            const wf = workflows.find((w) => w.workflow_id === v);
            if (wf) pickWorkflow(wf);
          }}>
            <SelectTrigger className="rounded-sm w-[240px]" data-testid="workflow-picker"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {workflows.map((w) => (
                <SelectItem key={w.workflow_id} value={w.workflow_id}>
                  {w.name} {w.is_default && "· default"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="workflow-name" />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Checkbox id="isdefault" checked={isDefault} onCheckedChange={setIsDefault} data-testid="workflow-default-check" />
          <label htmlFor="isdefault" className="text-xs uppercase tracking-wider">Set as default</label>
        </div>
        <Button onClick={createNew} variant="outline" className="rounded-sm" data-testid="workflow-new-btn">
          <Plus size={14} className="mr-1" /> New
        </Button>
        <Button onClick={save} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid="workflow-save-btn">
          <FloppyDisk size={14} className="mr-1" /> {busy ? "Saving…" : "Save"}
        </Button>
        <Button onClick={deleteWorkflow} variant="outline" className="rounded-sm text-red-600 border-red-200" data-testid="workflow-delete-btn">
          <Trash size={14} className="mr-1" /> Delete
        </Button>
      </div>

      {/* Add-status row */}
      <div className="border border-border bg-background p-3 flex flex-wrap gap-2 items-end">
        <div className="space-y-1 flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Add status node</Label>
          <div className="flex gap-2">
            <Input value={newStatus} onChange={(e) => setNewStatus(e.target.value)} placeholder="e.g. QA Review" className="rounded-sm" data-testid="new-status-input" onKeyDown={(e) => e.key === "Enter" && addStatusNode()} />
            <Button onClick={addStatusNode} variant="outline" className="rounded-sm" data-testid="add-status-btn">Add</Button>
          </div>
        </div>
        <Button onClick={deleteSelected} variant="outline" className="rounded-sm" data-testid="delete-selected-btn">
          <Trash size={14} className="mr-1" /> Delete selected
        </Button>
        <div className="text-xs text-zinc-500 ml-auto pb-2">
          Drag between nodes to create a transition · Click node/edge to select · Del key removes
        </div>
      </div>

      {/* Canvas */}
      <div className="border border-border bg-background" style={{ height: 560 }} data-testid="workflow-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={(_, edge) => setSelectedEdge(edge)}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} size={1} color="#E4E4E7" />
          <Controls />
          <MiniMap pannable zoomable className="!bg-background" />
        </ReactFlow>
      </div>

      <div className="text-xs text-zinc-500 border border-dashed border-border p-3 bg-surface/40">
        <strong>How it works:</strong> Nodes represent task statuses. Edges represent allowed transitions.
        Currently the workflow is <em>visualized and stored</em> — it will drive runtime status enforcement in the next release.
      </div>
    </div>
  );
}
